import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";
import { 
  Cable, Server, Shield, Camera, Lock, Phone, Mail, MapPin, 
  Menu, X, ChevronRight, CheckCircle, Star, Building2, ArrowRight,
  Wifi, HardDrive, Network, Globe2
} from "lucide-react";
import DeployPage from "./pages/DeployPage";
import AdminPage from "./pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Site images (CMS-uploaded) context.
// Ships with baked-in defaults so the site renders fully on first paint,
// even when the CMS/backend is unreachable (e.g. static cPanel hosting).
const REMOVED_GALLERY_IDS = new Set([26, 34]);
const DEFAULT_SITE_IMAGES = {
  "hero-2": "/uploads/hero-2.jpg",
  "hero-3": "/uploads/hero-3.jpg",
  "hero-4": "/uploads/hero-4.jpg",
  ...Object.fromEntries(
    Array.from({ length: 34 }, (_, i) => i + 1)
      .filter((n) => !REMOVED_GALLERY_IDS.has(n))
      .map((n) => [`gallery-${n}`, `/uploads/gallery-${n}.jpg`])
  ),
};

const SiteImagesContext = createContext(DEFAULT_SITE_IMAGES);
const useSiteImages = () => useContext(SiteImagesContext);

function SiteImagesProvider({ children }) {
  const [images, setImages] = useState(DEFAULT_SITE_IMAGES);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/site-images`, { timeout: 4000 });
        if (cancelled) return;
        const incoming = res.data || {};
        // Only override defaults when the CMS actually returned something
        if (Object.keys(incoming).length > 0) {
          setImages((prev) => ({ ...prev, ...incoming }));
        }
      } catch (e) {
        // Keep defaults silently — no flicker.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <SiteImagesContext.Provider value={images}>
      {children}
    </SiteImagesContext.Provider>
  );
}

// Design assets
const ASSETS = {
  hero: "https://static.prod-images.emergentagent.com/jobs/cee10f43-3191-44af-9fd6-80eafd8f93e7/images/052625de3b9350c921f7b81b575d665647ace408f118bc9b81955096709b5de6.png",
  cabling: "https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwY2FibGV8ZW58MHx8fHwxNzc1NjY3NzkxfDA&ixlib=rb-4.1.0&q=85",
  surveillance: "https://images.unsplash.com/photo-1672073311074-f60c4a5e7b92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxzdXJ2ZWlsbGFuY2UlMjBzZWN1cml0eSUyMGNhbWVyYXxlbnwwfHx8fDE3NzU2Njc3Njh8MA&ixlib=rb-4.1.0&q=85",
  infrastructure: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tfGVufDB8fHx8MTc3NTY2Nzc5MXww&ixlib=rb-4.1.0&q=85",
  resort1: "https://images.unsplash.com/photo-1744074026552-626d42d3d613?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBhcmNoaXRlY3R1cmUlMjBiYWhhbWFzfGVufDB8fHx8MTc3NTY2Nzc2NXww&ixlib=rb-4.1.0&q=85",
  resort2: "https://images.unsplash.com/photo-1717465760702-e2c057b0268f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjByZXNvcnQlMjBhcmNoaXRlY3R1cmUlMjBiYWhhbWFzfGVufDB8fHx8MTc3NTY2Nzc2NXww&ixlib=rb-4.1.0&q=85",
  corporate: "https://images.pexels.com/photos/29931645/pexels-photo-29931645.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hospital: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800",
  bank: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=800"
};

// Services data - Updated with full descriptions from PDF
const SERVICES = [
  { id: 1, title: "Fiber Optic Cabling", icon: Cable, description: "We deliver advanced fiber optic infrastructure designed to support high-capacity, high-speed data transmission. Our team specializes in the design, installation, and maintenance of scalable fiber systems, ensuring precision engineering, minimal signal loss, and long-term reliability across enterprise environments. We are also certified in fiber end terminations and splicing.", image: "/uploads/service-fiber.jpg" },
  { id: 2, title: "Copper Cabling for Voice & Data", icon: Network, description: "We provide structured copper cabling solutions that form the backbone of reliable voice and data communication systems. Utilizing industry-standard practices and premium-grade materials, our installations are executed with careful attention to organization, performance, and future scalability.", image: "/uploads/service-cabling.jpg" },
  { id: 3, title: "Service & Repairs", icon: Shield, description: "Our service and maintenance division ensures the continued performance and integrity of your systems. We provide efficient fault diagnosis, system repairs, and preventative maintenance, minimizing operational disruptions while maintaining the highest standards of technical execution for all services.", image: "/uploads/service-repairs.jpg" },
  { id: 4, title: "Surveillance Camera Installation", icon: Camera, description: "Islandwide Cabling designs and implements integrated surveillance systems tailored to each client's security requirements. Our installations are strategically planned to maximize coverage, enhance monitoring capabilities, and support seamless system operation across residential, commercial, and industrial environments.", image: "/uploads/service-surveillance.jpg" },
  { id: 5, title: "Access Control Systems", icon: Lock, description: "We implement secure intelligent access control systems that enable organizations to manage and monitor entry points with precision. Our solutions range from card/key fob-based systems to advanced biometric technologies, including keypad and badge-swipe entry.", image: "/uploads/service-access-control.jpg" },
  { id: 6, title: "UPS Systems", icon: HardDrive, description: "To safeguard critical infrastructure, we provide reliable UPS solutions designed to ensure continuity during power interruptions. Our cabinet-mounted UPS systems protect sensitive equipment from power instabilities.", image: "/uploads/service-ups.jpg" }
];

// Team members data - 3 confirmed members per latest client feedback
const TEAM_MEMBERS = [
  { id: 1, name: "Timothy Walker", role: "President & Founder" },
  { id: 2, name: "Nikkea Jarrett", role: "Executive Personal Assistant to the President" },
  { id: 3, name: "Lorenzo Flowers", role: "Sales & Marketing Manager" }
];

// Updated Projects data with real content and images from PDF
const PROJECTS = [
  { 
    id: 1, 
    name: "Atlantis Paradise Island", 
    category: "Hospitality",
    description: "ICC provides ongoing infrastructure support and system enhancements across Atlantis Paradise Island resort, one of the Caribbean's premier hospitality destinations. Our scope includes the deployment of comprehensive Wi-Fi coverage, structured voice and data cabling systems, and integrated surveillance infrastructure. We deliver both CAT6 and Fiber Optic solutions ensuring high-speed connectivity, system reliability, and seamless performance under peak demand conditions. Our team provides ongoing service room maintenance and repairs, ensuring core infrastructure remains fully operational and aligned with the resort's performance standards.",
    image: "/atlantis.png?v=1"
  },
  { 
    id: 2, 
    name: "Baha Mar Resort & Casino", 
    category: "Hospitality",
    description: "ICC played a key role in executing multiple infrastructure projects within the Baha Mar development, operating across three project phases simultaneously. The scope of work included structured cabling installations throughout water park facilities, kiosks, towel huts, wireless access points, micros terminals and complete voice and data port connectivity. Working in close coordination with the Baha Mar I.T team, ICC facilitated the installation of new uplinks and the termination of fiber optic systems across various areas of the property. Despite the scale and complexity, all work was completed on schedule and within budget.",
    solution: "CommScope Structured Cabling System",
    image: "/bahamar.png?v=1"
  },
  { 
    id: 3, 
    name: "Doctors Hospital Harborside", 
    category: "Healthcare",
    description: "ICC delivered a comprehensive infrastructure solution for Doctors Hospital, executing a full CommScope structured system across the campus. The project encompassed complete voice and data cabling for offices, workstations and patient areas, supported by fiber optic uplinks and a dedicated fiber ring for the computer room. ICC implemented access control systems, surveillance infrastructure, wireless access points, and UPS systems. Extensive trenching, directional drilling and installation of two-inch conduits supported underground infrastructure. Notably, we deployed a signal booster system to enhance internal network performance throughout the hospital environment.",
    image: "/doctors-hospital.png?v=1"
  },
  { 
    id: 4, 
    name: "Central Bank of The Bahamas", 
    category: "Financial",
    description: "ICC executed a sensitive surveillance and network infrastructure upgrade for the Central Bank of the Bahamas. This project involved deployment and termination of single-mode fiber to support secure, high-capacity data transmission. Works also included the installation of fiber enclosures, patch panels and supporting hardware, along with cabinet upgrades to accommodate new equipment requirements. The completed infrastructure delivers a secure, resilient and scalable network environment aligned with the performance and security demands of a national banking authority.",
    image: "/central-bank.png?v=1"
  },
  { 
    id: 5, 
    name: "Ministry of Works Thompson Boulevard", 
    category: "Government",
    description: "ICC was entrusted with the complete modernization of the network infrastructure at Ministry of Works' Thompson Boulevard branch. The engagement required the full decommissioning of legacy cabling systems and the deployment of a new, structured voice and data network across the facility. Every aspect of the installation was executed with precision, ensuring a clean transition into a fully upgraded environment. Through careful planning and controlled execution, all works were carried out without service disruption, maintaining continuity for both clients and internal operations.",
    image: "/ministry-of-works.png?v=1"
  }
];

// Testimonials data
const TESTIMONIALS = [
  { id: 1, name: "IT Director", role: "Atlantis Paradise Island", quote: "IslandWide delivers world-class infrastructure solutions that meet our high-demand operations. Their CAT6 and Fiber Optic solutions ensure seamless performance." },
  { id: 2, name: "Facilities Manager", role: "Doctors Hospital", quote: "The comprehensive infrastructure solution ICC delivered has streamlined our operations and reinforced long-term reliability across our facility." },
  { id: 3, name: "Project Manager", role: "Baha Mar", quote: "Despite the scale and complexity, ICC completed all work on schedule and within budget. Their coordination with our IT team was exceptional." },
  { id: 4, name: "Network Administrator", role: "Central Bank of The Bahamas", quote: "ICC delivered a secure, resilient network environment that meets the demanding standards of our national banking operations." },
  { id: 5, name: "Sebastian Curry", role: "AVP Information Technology, Equity Bank & Trust", quote: "Islandwide consistently delivers exceptional service, demonstrating professionalism and precision in meeting banking requirements. Their high standards ensure reliability and instill confidence within banking departments." }
];

// Partners - Updated with 3X Logic
const PARTNERS = ["COMMSCOPE", "VERTIV", "ANIXTER", "BELDEN", "AXIS", "3X LOGIC"];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Logo Component
function Logo({ light = false }) {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
      <img 
        src="/logo.png?v=3" 
        alt="IslandWide Cabling & Consulting" 
        className="h-20 md:h-24 w-auto"
      />
    </Link>
  );
}

// Navigation Component
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (isAdmin) return null;

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/portfolio", label: "Notable Projects" },
    { to: "/about", label: "About Us" },
    { to: "/team", label: "Meet the Team" },
    { to: "/gallery", label: "Photo Gallery" }
  ];

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 nav-solid`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 md:h-28">
            <Logo light={!scrolled && isHome} />

            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors text-[#475569] hover:text-[#1E90DB] ${location.pathname === link.to ? "text-[#1E90DB]" : ""}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" data-testid="nav-cta-button" className="btn-primary text-sm">
                Request Service
              </Link>
            </div>

            <button
              data-testid="mobile-menu-button"
              className="md:hidden p-2"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="text-[#0A1F2E]" size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              data-testid="mobile-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-[280px] bg-[#0A1F2E] z-50 md:hidden"
            >
              <div className="p-6">
                <button
                  data-testid="mobile-menu-close"
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-6 right-6 text-white"
                >
                  <X size={24} />
                </button>
                <div className="mt-16 flex flex-col gap-6">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      onClick={() => setMobileOpen(false)}
                      className="text-white text-lg font-medium hover:text-[#4CAF50] transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    data-testid="mobile-nav-cta"
                    onClick={() => setMobileOpen(false)}
                    className="btn-primary text-center mt-4"
                  >
                    Request Service
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// Hero Section - 3-image auto-rotating slider with crossfade
function HeroSection() {
  const siteImages = useSiteImages();
  // Default hero slides (baked into the build → work on cPanel without a backend).
  // CMS-uploaded images take precedence when available.
  const slides = [
    siteImages["hero-1"] || ASSETS.hero,
    siteImages["hero-2"] || "/uploads/hero-2.jpg",
    siteImages["hero-3"] || "/uploads/hero-3.jpg",
    siteImages["hero-4"] || "/uploads/hero-4.jpg",
  ].filter(Boolean);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <section
      data-testid="hero-section"
      className="hero-section"
    >
      {/* Crossfading slide backgrounds */}
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          className="hero-bg"
          style={{ backgroundImage: `url(${slides[index]})` }}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.2, ease: "easeInOut" }, scale: { duration: 7, ease: "linear" } }}
        />
      </AnimatePresence>
      <div className="hero-overlay" />

      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl"
          >
            <motion.p variants={fadeInUp} className="label-tag text-[#4CAF50] mb-6">
              Your Partner of Choice
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight tracking-tight mb-6"
            >
              <span className="inline-block">
                {"Reliable. Scalable.".split(" ").map((word, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-block mr-3"
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
              <br />
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="relative inline-block text-[#4CAF50]"
              >
                High-Performance.
                <motion.span
                  aria-hidden="true"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.2, delay: 1.2, ease: "easeInOut" }}
                  className="absolute left-0 -bottom-1 h-[6px] w-full origin-left bg-gradient-to-r from-[#4CAF50] via-[#1E90DB] to-transparent rounded-full opacity-70"
                />
              </motion.span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-white/80 max-w-2xl mb-10"
            >
              Our aim is to provide reliable, cost-effective, comprehensive solutions with efficient service to assist you in building an I.T. infrastructure you can depend on.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary flex items-center gap-2">
                Get Started <ArrowRight size={18} />
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-secondary" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0A1F2E]">
                View Projects
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Slide dots */}
      {slides.length > 1 && (
        <div
          data-testid="hero-slide-dots"
          className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-20 flex gap-3"
        >
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-slide-dot-${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all duration-500 ${
                i === index ? "w-10 bg-[#4CAF50]" : "w-2 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}

      {/* Partners Strip - auto-scrolling marquee */}
      <div className="absolute bottom-0 left-0 right-0 bg-white/5 backdrop-blur-sm border-t border-white/10 z-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center gap-6">
          <span className="text-white/50 text-xs uppercase tracking-wider whitespace-nowrap shrink-0">Strategic Partners</span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex animate-marquee gap-12 items-center w-max">
              {[...PARTNERS, ...PARTNERS, ...PARTNERS].map((partner, i) => (
                <span
                  key={`${partner}-${i}`}
                  className="partner-logo text-white/50 hover:text-white font-bold text-sm tracking-wider whitespace-nowrap transition-colors"
                >
                  {partner}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Services Preview Section
function ServicesPreview() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section data-testid="services-preview" ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.p variants={fadeInUp} className="label-tag mb-4">
            What We Do
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1F2E] mb-6">
            Comprehensive <span className="text-[#1E90DB]">IT Solutions</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#475569] max-w-2xl mb-16">
            From fiber optic installation to complete security systems, we provide 
            end-to-end infrastructure services for businesses of all sizes.
          </motion.p>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((service) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                data-testid={`service-card-${service.id}`}
                className="service-card"
              >
                <service.icon className="text-[#1E90DB] mb-4" size={32} />
                <h3 className="text-xl font-bold text-[#0A1F2E] mb-2">{service.title}</h3>
                <p className="text-[#475569]">{service.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <Link to="/services" data-testid="services-view-all" className="btn-secondary inline-flex items-center gap-2">
              View All Services <ChevronRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Projects Preview Section
function ProjectsPreview() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section data-testid="projects-preview" ref={ref} className="py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.p variants={fadeInUp} className="label-tag mb-4">
            Our Work
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1F2E] mb-6">
            Notable <span className="text-[#4CAF50]">Projects</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#475569] max-w-2xl mb-16">
            We've had the privilege of working with the Bahamas' most iconic establishments, 
            delivering infrastructure that powers their operations.
          </motion.p>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROJECTS.slice(0, 3).map((project) => (
              <motion.div
                key={project.id}
                variants={fadeInUp}
                data-testid={`project-card-${project.id}`}
                className="project-card aspect-[4/3]"
              >
                <img 
                  src={project.image} 
                  alt={project.name}
                  className="w-full h-full object-cover"
                />
                <div className="project-card-overlay">
                  <span className="label-tag text-[#4CAF50] mb-2">{project.category}</span>
                  <h3 className="text-2xl font-bold text-white">{project.name}</h3>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeInUp} className="mt-12 text-center">
            <Link to="/portfolio" data-testid="projects-view-all" className="btn-primary inline-flex items-center gap-2">
              View All Projects <ChevronRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section data-testid="testimonials-section" ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.p variants={fadeInUp} className="label-tag mb-4">
            Testimonials
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1F2E] mb-16">
            What Our <span className="text-[#1E90DB]">Clients Say</span>
          </motion.h2>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial) => (
              <motion.div
                key={testimonial.id}
                variants={fadeInUp}
                data-testid={`testimonial-card-${testimonial.id}`}
                className="testimonial-card"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#4CAF50] text-[#4CAF50]" />
                  ))}
                </div>
                <p className="text-[#475569] mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-bold text-[#0A1F2E]">{testimonial.name}</p>
                  <p className="text-sm text-[#64748B]">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  return (
    <section data-testid="cta-section" className="py-24 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-6">
          Ready to Build <span className="text-[#4CAF50]">Better</span>?
        </h2>
        <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">
          Let's discuss how we can help upgrade your infrastructure. 
          Contact us today for a free consultation.
        </p>
        <Link to="/contact" data-testid="cta-button" className="btn-primary inline-flex items-center gap-2">
          Get in Touch <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  );
}

// Footer Component
function Footer() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return (
    <footer data-testid="footer" className="footer-section py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <img 
              src="/logo-footer.png?v=1" 
              alt="IslandWide Cabling & Consulting" 
              className="h-40 md:h-48 w-auto mb-6"
            />
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="text-white/60 hover:text-[#4CAF50] transition-colors">Home</Link>
              <Link to="/services" className="text-white/60 hover:text-[#4CAF50] transition-colors">Services</Link>
              <Link to="/portfolio" className="text-white/60 hover:text-[#4CAF50] transition-colors">Notable Projects</Link>
              <Link to="/about" className="text-white/60 hover:text-[#4CAF50] transition-colors">About Us</Link>
              <Link to="/team" className="text-white/60 hover:text-[#4CAF50] transition-colors">Meet the Team</Link>
              <Link to="/contact" className="text-white/60 hover:text-[#4CAF50] transition-colors">Request Service</Link>
              <Link to="/gallery" className="text-white/60 hover:text-[#4CAF50] transition-colors">Photo Gallery</Link>
            </nav>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Contact</h4>
            <address className="not-italic text-white/60 space-y-2">
              <p>#85 Crawford Street<br/>Crawford House, Nassau</p>
              <p>+1 (242) 601-8891/2</p>
              <p>info@iccbahamas.com</p>
              <p>service@iccbahamas.com</p>
            </address>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8">
          <p className="text-white/40 text-sm text-center">
            © {new Date().getFullYear()} IslandWide Cabling & Consulting Services Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Home Page
function HomePage() {
  return (
    <>
      <HeroSection />
      <StatsSection />
      <WhatWeDoSection />
      <TestimonialsSection />
      <CTASection />
    </>
  );
}

// Animated count-up stat
function CountUp({ to, duration = 2, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

function StatsSection() {
  const stats = [
    { type: "number", label: "Years of Proven Experience", to: 50, suffix: "+" },
    { type: "number", label: "Major Projects Delivered Across The Bahamas", to: 1000, suffix: "+" },
    { type: "emblem", label: "Trusted by Government & Enterprise Clients", image: "/uploads/trusted-emblem.png" },
    { type: "iconImage", label: "Nationwide IT & Infrastructure Coverage", image: "/uploads/stat-coverage.png" },
  ];
  return (
    <section data-testid="stats-section" className="relative py-20 bg-[#0A1F2E] overflow-hidden">
      <div className="absolute inset-0 opacity-15">
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-[#1E90DB] rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-[#4CAF50] rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="text-center flex flex-col items-center"
            >
              {s.type === "number" && (
                <p className="text-5xl md:text-6xl font-black text-white tracking-tight h-20 md:h-24 flex items-center">
                  <CountUp to={s.to} suffix={s.suffix} />
                </p>
              )}
              {s.type === "image" && (
                <div className="h-20 md:h-24 w-20 md:w-24 rounded-full ring-2 ring-[#4CAF50]/50 shadow-[0_0_24px_rgba(76,175,80,0.25)] overflow-hidden">
                  <img src={s.image} alt={s.label} className="w-full h-full object-cover" />
                </div>
              )}
              {s.type === "emblem" && (
                <div className="h-20 md:h-24 w-24 md:w-28 flex items-center justify-center">
                  <img
                    src={s.image}
                    alt={s.label}
                    className="max-w-full max-h-full object-contain drop-shadow-[0_0_18px_rgba(76,175,80,0.5)]"
                  />
                </div>
              )}
              {s.type === "iconImage" && (
                <div className="h-20 md:h-24 w-20 md:w-24 flex items-center justify-center">
                  <img src={s.image} alt={s.label} className="max-w-full max-h-full object-contain drop-shadow-[0_0_12px_rgba(30,144,219,0.5)]" />
                </div>
              )}
              <p className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#4CAF50] font-semibold leading-tight max-w-[220px]">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// What We Do Section (simplified for homepage)
function WhatWeDoSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section data-testid="what-we-do" ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.p variants={fadeInUp} className="label-tag mb-4">
            What We Do
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1F2E] mb-6">
            Comprehensive <span className="text-[#1E90DB]">IT Solutions</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#475569] max-w-3xl mx-auto mb-10">
            From fiber optic installation to complete security systems, we provide 
            end-to-end infrastructure services for businesses of all sizes. Our team 
            delivers reliable, scalable solutions backed by over 55 years of combined experience.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/services" className="btn-primary inline-flex items-center gap-2">
              View Our Services <ChevronRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Services Page
function ServicesPage() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <>
      <section data-testid="services-hero" className="pt-32 pb-16 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="label-tag text-[#4CAF50] mb-4">Our Services</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-6">
            What We <span className="text-[#4CAF50]">Offer</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            Comprehensive IT infrastructure solutions tailored to your business needs.
          </p>
        </div>
      </section>

      <section ref={ref} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="space-y-12"
          >
            {SERVICES.map((service, index) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                data-testid={`service-detail-${service.id}`}
                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
              >
                {/* Image */}
                <div className="w-full md:w-1/2">
                  <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                {/* Content */}
                <div className="w-full md:w-1/2">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1E90DB]/10 to-[#4CAF50]/10 rounded-lg flex items-center justify-center">
                      <service.icon className="text-[#1E90DB]" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1F2E]">{service.title}</h3>
                  </div>
                  <p className="text-[#475569] mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-[#475569]">
                      <CheckCircle size={16} className="text-[#4CAF50]" />
                      Professional installation
                    </li>
                    <li className="flex items-center gap-2 text-sm text-[#475569]">
                      <CheckCircle size={16} className="text-[#4CAF50]" />
                      24/7 support available
                    </li>
                    <li className="flex items-center gap-2 text-sm text-[#475569]">
                      <CheckCircle size={16} className="text-[#4CAF50]" />
                      Industry certifications
                    </li>
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CTASection />
    </>
  );
}

// Portfolio Page
function PortfolioPage() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <>
      <section data-testid="portfolio-hero" className="pt-32 pb-16 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="label-tag text-[#4CAF50] mb-4">Our Portfolio</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-6">
            Notable <span className="text-[#4CAF50]">Projects</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            Trusted by the Bahamas' most prestigious organizations.
          </p>
        </div>
      </section>

      <section ref={ref} className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="space-y-8"
          >
            {PROJECTS.map((project) => (
              <motion.div
                key={project.id}
                variants={fadeInUp}
                data-testid={`portfolio-project-${project.id}`}
                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="md:flex">
                  <div className="md:w-1/3">
                    <img 
                      src={project.image} 
                      alt={project.name}
                      className="w-full h-64 md:h-full object-cover"
                    />
                  </div>
                  <div className="md:w-2/3 p-8">
                    <span className="label-tag mb-2 inline-block">{project.category}</span>
                    <h3 className="text-2xl font-bold text-[#0A1F2E] mb-4">{project.name}</h3>
                    <p className="text-[#475569] mb-6">{project.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-[#1E90DB]/10 text-[#1E90DB] text-sm rounded-full">Fiber Optics</span>
                      <span className="px-3 py-1 bg-[#4CAF50]/10 text-[#4CAF50] text-sm rounded-full">Structured Cabling</span>
                      <span className="px-3 py-1 bg-[#0A1F2E]/10 text-[#0A1F2E] text-sm rounded-full">Network Infrastructure</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Clients Logos */}
      <section data-testid="trusted-by-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col items-center mb-12">
            <p className="label-tag text-center">Trusted By</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-10 items-center">
            {[
              { name: "Atlantis", src: "/uploads/trusted-atlantis.png" },
              { name: "Baha Mar", src: "/uploads/trusted-bahamar.png" },
              { name: "Central Bank of The Bahamas", src: "/uploads/trusted-central-bank.png" },
              { name: "Doctors Hospital", src: "/uploads/trusted-doctors-hospital.png" },
              { name: "Ministry of Works", src: "/uploads/trusted-ministry-of-works.png" },
            ].map((client, i) => (
              <motion.div
                key={client.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-center justify-center h-24 md:h-28 group"
              >
                <img
                  src={client.src}
                  alt={client.name}
                  loading="lazy"
                  className="max-h-full max-w-[80%] object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}

// About Us Page
function AboutPage() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="label-tag text-[#4CAF50] mb-4">About Us</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-6">
            Building I.T. Infrastructure <span className="text-[#4CAF50]">You Can Depend On</span>
          </h1>
        </div>
      </section>

      <section ref={ref} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="space-y-16"
          >
            {/* Company Overview */}
            <motion.div variants={fadeInUp} className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#0A1F2E] mb-6">Company Overview</h2>
                <p className="text-[#475569] leading-relaxed">
                  Islandwide Cabling & Consulting is a leading provider of structured cabling and network infrastructure solutions in The Bahamas. As a subsidiary of T & T Consulting Services Limited, the company was established with a clear objective: <span className="text-[#4CAF50] font-semibold">to deliver reliable, scalable, and high-performance infrastructure solutions that support modern business operations.</span>
                </p>
                <p className="text-[#475569] leading-relaxed mt-4">
                  With the continued evolution of IP-based technologies, the demand for stable and efficient network systems has never been greater. Islandwide is positioned at the forefront of this shift, providing expertly designed and implemented cabling systems that form the backbone of critical communications.
                </p>
              </div>
              <div className="bg-gradient-to-br from-[#1E90DB]/10 to-[#4CAF50]/10 p-8 rounded-lg">
                <img src={ASSETS.infrastructure} alt="Infrastructure" className="w-full h-64 object-cover rounded-lg" />
              </div>
            </motion.div>

            {/* Our Approach */}
            <motion.div variants={fadeInUp} className="bg-[#F8F9FA] p-8 rounded-lg">
              <h2 className="text-3xl font-bold text-[#0A1F2E] mb-6">Our Approach</h2>
              <p className="text-[#475569] leading-relaxed">
                We take a structured and detail-oriented approach to every project, ensuring that each solution is designed with long-term performance, scalability, and efficiency in mind. From initial consultation to final implementation, <span className="text-[#4CAF50] font-semibold">our focus remains on precision, quality, and seamless execution.</span>
              </p>
              <p className="text-[#475569] leading-relaxed mt-4">
                Our team works closely with clients to understand their operational requirements, allowing us to deliver tailored solutions that align with both current needs and future growth.
              </p>
            </motion.div>

            {/* Our Commitment - Center Aligned */}
            <motion.div variants={fadeInUp} className="text-center">
              <h2 className="text-3xl font-bold text-[#0A1F2E] mb-6">Our Commitment</h2>
              <p className="text-[#475569] leading-relaxed max-w-3xl mx-auto">
                Our commitment is to deliver reliable, cost-effective solutions while maintaining the highest standards of workmanship and service delivery. We continue to stand by our guiding principle:
              </p>
              <p className="text-2xl font-bold text-[#4CAF50] mt-4">
                "Building I.T. Infrastructure You Can Depend On"
              </p>
            </motion.div>

            {/* Our Experience */}
            <motion.div variants={fadeInUp} className="bg-[#0A1F2E] text-white p-8 rounded-lg">
              <h2 className="text-3xl font-bold mb-6">Our Experience</h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Islandwide Cabling & Consulting is backed by a team with over 55 years of combined industry experience. Our technical expertise spans a wide range of globally recognized systems and standards:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Ortronics Open Architecture Structured Cabling Systems",
                  "Nordx/CDT Certified Structured Cabling Solutions",
                  "Panduit Certified Cabling Systems",
                  "Uniprise Cabling Solutions",
                  "Certified Fiber Optics Installations (ETA)",
                  "SYSTIMAX & CommScope Installations",
                  "AXIS Camera Systems",
                  "Infinias Access Control & Video Management Systems"
                ].map((cert, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="text-[#4CAF50] flex-shrink-0" size={18} />
                    <span className="text-white/90 text-sm">{cert}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <CTASection />
    </>
  );
}

// Meet the Team Page
function TeamPage() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const siteImages = useSiteImages();
  const teamSlug = (name) => `team-${name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="label-tag text-[#4CAF50] mb-4">Our Team</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-6">
            Meet the <span className="text-[#4CAF50]">Team</span>
          </h1>
          <p className="text-lg text-white/70 max-w-3xl">
            Our team consists of highly trained and certified professionals with extensive experience in structured cabling and network infrastructure. Led by a president with a strong technical foundation in computer engineering and industry certifications, Islandwide maintains a workforce capable of executing projects with precision, efficiency and professionalism.
          </p>
        </div>
      </section>

      <section ref={ref} className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {TEAM_MEMBERS.map((member) => {
              const photo = siteImages[teamSlug(member.name)];
              return (
                <motion.div
                  key={member.id}
                  variants={fadeInUp}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="h-48 bg-gradient-to-br from-[#1E90DB]/20 to-[#4CAF50]/20 flex items-center justify-center">
                    {photo ? (
                      <img src={photo} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-24 h-24 bg-[#0A1F2E] rounded-full flex items-center justify-center text-white text-3xl font-bold">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    )}
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-bold text-[#0A1F2E]">{member.name}</h3>
                    <p className="text-sm text-[#64748B] mt-1">{member.role}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Company Fleet Section */}
      <section data-testid="company-fleet-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="label-tag mb-4">On The Road</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1F2E] mb-6">
              Our <span className="text-[#1E90DB]">Company Fleet</span>
            </h2>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto">
              A dedicated fleet of service vehicles ready to deploy across The Bahamas, ensuring rapid response and full on-site capability for every project.
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="fleet-showcase"
            className="relative overflow-hidden rounded-2xl shadow-2xl group"
          >
            <img
              src={siteImages["fleet-showcase"] || "/uploads/hero-3.jpg"}
              alt="IslandWide Cabling & Consulting company fleet"
              className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-[1200ms] ease-out"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F2E]/70 via-[#0A1F2E]/10 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <p className="text-[#4CAF50] text-xs uppercase tracking-[0.3em] mb-2">Service Fleet</p>
              <h3 className="text-white text-2xl md:text-3xl font-bold">
                Ready to roll out across The Bahamas.
              </h3>
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection />
    </>
  );
}


// Contact Page
function ContactPage() {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", 
    infrastructure_type: "", service_type: "", message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/service-requests`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", company: "", service_type: "general", message: "" });
    } catch (err) {
      setError("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section data-testid="contact-hero" className="pt-32 pb-16 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="label-tag text-[#4CAF50] mb-4">Contact Us</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-6">
            Let's <span className="text-[#4CAF50]">Talk</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            Ready to upgrade your infrastructure? Get in touch with our team.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#0A1F2E] mb-8">Get in Touch</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1E90DB]/10 to-[#4CAF50]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#1E90DB]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A1F2E] mb-1">Address</h3>
                    <p className="text-[#475569]">#85 Crawford Street, Crawford House<br/>Nassau, Bahamas</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1E90DB]/10 to-[#4CAF50]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#1E90DB]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A1F2E] mb-1">Phone</h3>
                    <a href="tel:+12426018891" className="text-[#475569] hover:text-[#1E90DB]">+1 (242) 601-8891/2</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#1E90DB]/10 to-[#4CAF50]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#1E90DB]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A1F2E] mb-1">Email</h3>
                    <a href="mailto:info@iccbahamas.com" className="text-[#475569] hover:text-[#1E90DB] block">info@iccbahamas.com</a>
                    <a href="mailto:service@iccbahamas.com" className="text-[#475569] hover:text-[#1E90DB]">service@iccbahamas.com</a>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed - Pin at exact ICC coordinates (Crawford Street, Nassau) */}
              <div data-testid="google-maps-embed" className="map-container">
                <iframe
                  src="https://www.google.com/maps?q=25.057516,-77.368602&hl=en&z=18&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="IslandWide Cabling & Consulting - 85 Crawford Street, Nassau"
                />
              </div>
            </div>

            {/* Service Request Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#0A1F2E] mb-8">Request Service</h2>
              
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="form-success-message"
                  className="success-message"
                >
                  <CheckCircle size={48} className="mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Request Submitted!</h3>
                  <p>We'll get back to you within 24 hours.</p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="mt-4 underline"
                  >
                    Submit another request
                  </button>
                </motion.div>
              ) : (
                <form data-testid="service-request-form" onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Name *</label>
                      <input
                        type="text"
                        data-testid="form-name"
                        required
                        className="form-input"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Email *</label>
                      <input
                        type="email"
                        data-testid="form-email"
                        required
                        className="form-input"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Phone</label>
                      <input
                        type="tel"
                        data-testid="form-phone"
                        className="form-input"
                        placeholder="+1 (242) 000-0000"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Company</label>
                      <input
                        type="text"
                        data-testid="form-company"
                        className="form-input"
                        placeholder="Your company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Infrastructure Type *</label>
                    <select
                      data-testid="form-infrastructure-type"
                      required
                      className="form-input"
                      value={formData.infrastructure_type}
                      onChange={(e) => setFormData({ ...formData, infrastructure_type: e.target.value })}
                    >
                      <option value="">Select Infrastructure Type</option>
                      <option value="CAT6">CAT6</option>
                      <option value="CAT6A">CAT6A</option>
                      <option value="Fiber">Fiber</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Service Type *</label>
                    <select
                      data-testid="form-service-type"
                      required
                      className="form-input"
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    >
                      <option value="">Select Service Type</option>
                      <option value="troubleshoot">Troubleshoot & Repair</option>
                      <option value="installation">New Installation</option>
                      <option value="testing">Testing & Labelling</option>
                      <option value="reterminations">Re-terminations</option>
                      <option value="splicing">Splicing</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A1F2E] mb-2">Message *</label>
                    <textarea
                      data-testid="form-message"
                      required
                      rows={5}
                      className="form-input resize-none"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                  </div>

                  {error && (
                    <p data-testid="form-error" className="text-red-500 text-sm">{error}</p>
                  )}

                  <button
                    type="submit"
                    data-testid="form-submit"
                    disabled={submitting}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {submitting ? "Submitting..." : "Submit Request"}
                    {!submitting && <ArrowRight size={18} />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// Photo Gallery Page — sleek masonry + lightbox
function GalleryPage() {
  const siteImages = useSiteImages();
  // Default gallery photos (baked into build) → work on cPanel without backend.
  // CMS overrides take precedence when an admin is hosted.
  const photos = Array.from({ length: 50 }, (_, i) => {
    const idx = i + 1;
    const key = `gallery-${idx}`;
    if (siteImages[key]) return siteImages[key];
    if (idx <= 34 && !REMOVED_GALLERY_IDS.has(idx)) return `/uploads/gallery-${idx}.jpg`;
    return null;
  }).filter(Boolean);
  const [lightbox, setLightbox] = useState(null); // index

  // Close on Escape, navigate with arrow keys
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [lightbox, photos.length]);

  return (
    <>
      <section className="pt-32 pb-16 bg-gradient-to-r from-[#0A1F2E] via-[#0A4A7A] to-[#1E90DB] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#4CAF50] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#1E90DB] rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <p className="label-tag text-[#4CAF50] mb-4">Gallery</p>
          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter mb-6">
            Our Work In <span className="text-[#4CAF50]">Pictures</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            A visual journey through completed installations, on-site work, and the team behind every project.
          </p>
          {photos.length > 0 && (
            <p className="mt-6 inline-flex items-center gap-2 text-sm text-white/80 bg-white/10 backdrop-blur px-4 py-2 rounded-full border border-white/10">
              <Camera size={16} /> Click any image to view full-size
            </p>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {photos.length === 0 ? (
            <div className="text-center">
              <div className="bg-white p-12 rounded-lg shadow-sm">
                <Camera className="mx-auto text-[#1E90DB] mb-6" size={64} />
                <h2 className="text-2xl font-bold text-[#0A1F2E] mb-4">Coming Soon</h2>
                <p className="text-[#475569] max-w-lg mx-auto">
                  We're currently curating our best project photos. Check back soon for a visual journey through our completed infrastructure projects.
                </p>
              </div>
            </div>
          ) : (
            <div
              data-testid="gallery-grid"
              className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 auto-rows-[140px] md:auto-rows-[180px] gap-3 md:gap-4"
            >
              {photos.map((src, i) => {
                // Bento pattern: every 7th cell is a "feature" (2x2), every 11th is wide (2x1), rest are 1x1
                const isFeature = i % 7 === 0;
                const isWide = i % 11 === 5;
                const isTall = i % 13 === 3;
                const spanClass = isFeature
                  ? "col-span-2 row-span-2"
                  : isWide
                  ? "col-span-2 row-span-1"
                  : isTall
                  ? "col-span-1 row-span-2"
                  : "col-span-1 row-span-1";
                return (
                  <motion.button
                    key={i}
                    type="button"
                    initial={{ opacity: 0, scale: 0.96 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: (i % 9) * 0.03 }}
                    onClick={() => setLightbox(i)}
                    data-testid={`gallery-thumb-${i + 1}`}
                    className={`${spanClass} group relative overflow-hidden rounded-xl bg-white shadow-sm hover:shadow-2xl transition-all duration-500 cursor-zoom-in border-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E90DB] p-0`}
                  >
                    <img
                      src={src}
                      alt={`ICC project photo ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover block group-hover:scale-[1.06] transition-transform duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F2E]/90 via-[#0A1F2E]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
                      <div className="text-white">
                        <p className="text-xs uppercase tracking-widest text-[#4CAF50] mb-1">View</p>
                        <p className="text-sm font-medium">Photo {i + 1} / {photos.length}</p>
                      </div>
                    </div>
                    {isFeature && (
                      <span className="absolute top-3 left-3 bg-[#4CAF50] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && photos[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              data-testid="lightbox-close"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              aria-label="Close"
            >
              <X size={22} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + photos.length) % photos.length); }}
              className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              aria-label="Previous"
            >
              <ChevronRight className="rotate-180" size={22} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % photos.length); }}
              className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
              aria-label="Next"
            >
              <ChevronRight size={22} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              src={photos[lightbox]}
              alt={`Photo ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm bg-white/5 px-4 py-2 rounded-full backdrop-blur">
              {lightbox + 1} / {photos.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTASection />
    </>
  );
}


// Main App
function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <SiteImagesProvider>
          <ScrollToTop />
          <Navigation />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/admin/deploy" element={<DeployPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </main>
          <Footer />
        </SiteImagesProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
